import ClassStringBuilder from 'core/utilities/class-string-builder';
import { ISharedSegmentedControl } from './segmented-control-interface';

/**
 * One control holding a few mutually exclusive views of the same list.
 *
 * Distinct from the dashboard's tabs on purpose: those switch between separate
 * screens, this switches between slices of one. And distinct from a row of
 * filter buttons, which is what it replaces — those looked like six independent
 * toggles when only one can ever be on, and gave no sense of being a single
 * choice.
 *
 * Counts belong on the segment rather than in a banner above it. A number next
 * to the label is the same information in the place you look before deciding
 * where to go, and it cannot fall out of step with what the segment shows.
 */
const SharedSegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  testIdPrefix,
  ariaLabel,
  size = 'default',
  disabled = false,
}: ISharedSegmentedControl<T>) => (
  <div
    role='tablist'
    aria-label={ariaLabel}
    className={ClassStringBuilder([
      'inline-flex flex-wrap gap-1 rounded-lg bg-gray-100',
      size === 'compact' ? 'p-0.5' : 'p-1',
    ])}
  >
    {options.map((option) => {
      const active = option.key === value;
      const unavailable = disabled || option.disabled;
      return (
        <button
          key={option.key}
          type='button'
          role='tab'
          aria-selected={active}
          disabled={unavailable}
          title={option.disabled ? option.disabledReason : undefined}
          data-testid={`${testIdPrefix}-${option.key}`}
          onClick={() => onChange(option.key)}
          className={ClassStringBuilder([
            'flex items-center gap-2 rounded-md transition',
            size === 'compact' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm',
            active ? 'bg-white font-semibold shadow-sm' : 'text-gray-600',
            unavailable ? 'cursor-not-allowed opacity-40' : 'hover:text-gray-900',
          ])}
        >
          <span>{option.label}</span>
          {option.count !== undefined && (
            <span
              data-testid={`${testIdPrefix}-${option.key}-count`}
              className={ClassStringBuilder([
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                option.emphasis === 'attention' && option.count > 0
                  ? 'bg-amber-100 text-amber-900'
                  : 'bg-gray-200 text-gray-700',
              ])}
            >
              {option.countLabel ?? option.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default SharedSegmentedControl;
